import type { GridColDef } from "@mui/x-data-grid-premium";
import { parseISO } from "date-fns";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { combineComparers, distinctBy, priorityComparer } from "@entrilia/shared/utilities/arrayHelper";
import { formatDateTime } from "@entrilia/shared/utilities/dateUtils";
import type { InvestorContact, InvestorFund, InvestorWithFieldValues } from "../../../../api/types/fundStructureTypes";
import type { Field } from "../../../../api/types/objectTypes";
import type { TableColumnDefinition } from "../../../common/columns/columnDefinitions";
import type { FilterState } from "../../../common/filters/filterState";
import { getAppliedMultiSelectFilterValues } from "../../../common/filters/filterState";
import { getGridColumnForField } from "../../../entityFields/fieldGridDefinitions";

export const getInvestorPath = (investorId: string): string => `./${investorId}/main`;

export const staticColumnDefinitions: TableColumnDefinition[] = [
  {
    id: "investorEntityType",
    title: "Entity Type",
  },
  {
    id: "funds",
    title: "Funds",
  },
  {
    id: "contacts",
    title: "Contacts",
  },
  {
    id: "createdAt",
    title: "Created at",
    defaultHidden: true,
  },
  {
    id: "updateAt",
    title: "Modified at",
    defaultHidden: true,
  },
];

const getSelectableGridColumns = (
  filterState: FilterState<InvestorWithFieldValues>
): GridColDef<InvestorWithFieldValues>[] => {
  const filterFundValues = getAppliedMultiSelectFilterValues("funds", filterState);
  const filterContactValues = getAppliedMultiSelectFilterValues("contacts", filterState);

  const fundsSortComparer = combineComparers(
    priorityComparer<string>((value) => filterFundValues.includes(value)),
    (a, b) => a.localeCompare(b)
  );

  const contactsSortComparer = combineComparers(
    priorityComparer<string>((value) => filterContactValues.includes(value)),
    (a, b) => a.localeCompare(b)
  );

  return [
    {
      field: "investorEntityType",
      headerName: "Entity Type",
      flex: 1,
    },
    {
      field: "funds",
      headerName: "Funds",
      flex: 1,
      renderCell: ({ row }) =>
        row.funds.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<InvestorFund>
            displayCount={1}
            items={distinctBy(row.funds, (f) => f.fundName)}
            propGetter={(prop) => prop.fundName}
            sortComparer={fundsSortComparer}
          />
        ),
    },
    {
      field: "contacts",
      headerName: "Contacts",
      flex: 1,
      renderCell: ({ row }) =>
        row.contacts.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<InvestorContact>
            displayCount={1}
            items={distinctBy(row.contacts, (c) => c.name)}
            propGetter={(prop) => prop.name}
            sortComparer={contactsSortComparer}
          />
        ),
    },
    {
      field: "createdAt",
      headerName: "Created at",
      type: "dateTime",
      flex: 1,
      valueGetter: (_, row) => {
        const createdDate = parseISO(row.createdAt);
        return createdDate;
      },
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "updateAt",
      headerName: "Modified at",
      type: "dateTime",
      flex: 1,
      valueGetter: (_, row) => {
        const updatedDate = parseISO(row.updateAt);
        return updatedDate;
      },
      valueFormatter: (value) => formatDateTime(value),
    },
  ];
};

export const getInvestorsGridColumns = (
  fields: Field[],
  filterState: FilterState<InvestorWithFieldValues>
): GridColDef<InvestorWithFieldValues>[] => {
  const selectableColumns = getSelectableGridColumns(filterState);

  const selectedColumns = filterState.visibleColumns.reduce<GridColDef<InvestorWithFieldValues>[]>((result, column) => {
    const gridColumn = selectableColumns.find((col) => col.field === column.id);
    if (gridColumn) {
      result.push(gridColumn);
    } else {
      const field = fields.find((field) => field.id === column.id);
      if (field) {
        result.push(getGridColumnForField(field));
      }
    }
    return result;
  }, []);

  return [
    {
      field: "title",
      headerName: "Name",
      flex: 1.5,
      renderCell: ({ row }) => <GridTextHoverLink to={getInvestorPath(row.id)} text={row.title} />,
    },
    ...selectedColumns,
  ];
};
